import {
  FundingPolicyType,
  unlimitedMaxBenefitMagicNumber,
  checkPolicyCalculationTypeEquals,
} from '@shared';

import { isFiniteNumber } from '@/features/policies-comparison';

export function calculateTotalBenefits(
  policyFundingSource: PolicyFundingSource,
  phaseCalculations: SinglePhaseCalculations[],
  firstCarePhaseIndex: number,
): {
  initialTotalBenefit?: number;
  inflatedTotalBenefit?: number;
} {
  const { policyBenefitIsUnlimited, policyMaximumBenefitAmount, policyContinuationBenefitAmount } =
    policyFundingSource;
  const initialTotalBenefit = policyBenefitIsUnlimited
    ? unlimitedMaxBenefitMagicNumber
    : (policyMaximumBenefitAmount ?? 0) + (policyContinuationBenefitAmount ?? 0);
  const firstCarePhaseCalc = Array.isArray(phaseCalculations)
    ? phaseCalculations[firstCarePhaseIndex]
    : undefined;
  const inflatedTotalBenefit = firstCarePhaseCalc?.inflatedMaxBenefitAmount ?? 0;

  if (
    checkPolicyCalculationTypeEquals(policyFundingSource.policyType, [
      FundingPolicyType.annuityHybrid,
    ]) ||
    policyFundingSource.policyType === FundingPolicyType.indexedAnnuityCare
  ) {
    return {
      initialTotalBenefit: undefined,
      inflatedTotalBenefit,
    };
  }
  return {
    initialTotalBenefit,
    inflatedTotalBenefit,
  };
}

export function calculateMonthlyBenefit(
  phaseCalcsForPolicy: SinglePhaseCalculations[] | undefined,
  daysPerMonthForCalc: number,
  policyType: FundingPolicyType,
  firstCarePhaseIndex?: number,
): {
  initialMonthlyBenefit?: number;
  inflatedMonthlyBenefit?: number;
  inflatedMonthlyBenefitBase?: number;
  inflatedMonthlyBenefitRider?: number;
  initialDailyBenefit?: number;
  inflatedDailyBenefit?: number;
} {
  let initialMonthlyBenefit: number | undefined;
  let inflatedMonthlyBenefit: number | undefined;
  let inflatedDailyBenefitBaseAmount: number | null;
  let inflatedDailyBenefitRiderAmount: number | null;
  let inflatedMonthlyBenefitBase: number | undefined;
  let inflatedMonthlyBenefitRider: number | undefined;
  let initialDailyBenefit: number | undefined;
  let inflatedDailyBenefit: number | undefined;

  const firstCarePhaseCalc =
    Array.isArray(phaseCalcsForPolicy) && isFiniteNumber(firstCarePhaseIndex)
      ? phaseCalcsForPolicy[firstCarePhaseIndex!]
      : undefined;

  if (firstCarePhaseCalc) {
    if (
      checkPolicyCalculationTypeEquals(policyType, [
        FundingPolicyType.hybridLifeIra,
        FundingPolicyType.hybridLifeInsurance,
      ])
    ) {
      initialDailyBenefit = firstCarePhaseCalc.dailyBenefitAmount;

      inflatedDailyBenefitRiderAmount = firstCarePhaseCalc.inflatedDailyBenefitAmountRider;
      inflatedDailyBenefitBaseAmount = checkPolicyCalculationTypeEquals(policyType, [
        FundingPolicyType.hybridLifeIra,
      ])
        ? firstCarePhaseCalc.dailyBenefitAmountBase
        : firstCarePhaseCalc.inflatedDailyBenefitAmountBase;

      if (isFiniteNumber(inflatedDailyBenefitBaseAmount)) {
        inflatedMonthlyBenefitBase = inflatedDailyBenefitBaseAmount! * daysPerMonthForCalc;
      }
      if (isFiniteNumber(inflatedDailyBenefitRiderAmount)) {
        inflatedMonthlyBenefitRider = inflatedDailyBenefitRiderAmount! * daysPerMonthForCalc;
      }

      const finalInflatedDailyBenefitAmount = Math.max(
        inflatedDailyBenefitBaseAmount ?? 0,
        inflatedDailyBenefitRiderAmount ?? 0,
        firstCarePhaseCalc.inflatedDailyBenefitAmount ?? 0,
      );
      inflatedDailyBenefit = finalInflatedDailyBenefitAmount;
    } else if (checkPolicyCalculationTypeEquals(policyType, [FundingPolicyType.annuityHybrid])) {
      initialDailyBenefit = undefined;
      inflatedDailyBenefit = firstCarePhaseCalc?.inflatedDailyBenefitAmount;
    } else {
      initialDailyBenefit = firstCarePhaseCalc.dailyBenefitAmount;
      inflatedDailyBenefit = firstCarePhaseCalc?.inflatedDailyBenefitAmount ?? initialDailyBenefit;
    }

    if (isFiniteNumber(initialDailyBenefit)) {
      initialMonthlyBenefit = initialDailyBenefit! * daysPerMonthForCalc;
    }

    if (isFiniteNumber(inflatedDailyBenefit)) {
      inflatedMonthlyBenefit = inflatedDailyBenefit! * daysPerMonthForCalc;
    }
  }

  return {
    initialMonthlyBenefit,
    inflatedMonthlyBenefit,
    inflatedMonthlyBenefitBase,
    inflatedMonthlyBenefitRider,
    initialDailyBenefit,
    inflatedDailyBenefit,
  };
}
